/**
 * ============================================================================
 * SOLA ⚡️ — GA4 EVENT ENGINE
 * ============================================================================
 * Camada de tracking GA4 do tema. Consome o contexto server-side montado em
 * snippets/sola-ga4.liquid (window.SolaGA4Data).
 *
 * PRINCÍPIO DE DESIGN: este arquivo não depende de nenhum código do tema.
 * Não escuta o pubsub interno (global.js não publica evento no add-to-cart),
 * não requer data-attributes nos cards e não precisa de edição nas sections.
 * Ele intercepta as chamadas à Cart API do Shopify, que é o único caminho por
 * onde todo add/remove passa — venha da PDP, do quick-add, do upsell do drawer
 * ou de um app de terceiro. Isso mantém o tracking vivo mesmo quando o tema
 * for atualizado e global.js for sobrescrito.
 *
 * Eventos cobertos (camada tema):
 *   page_view, view_item, view_item_list, select_item, add_to_cart,
 *   remove_from_cart, view_cart, begin_checkout, search,
 *   lead_form_view, lead_form_start, generate_lead, sign_up
 *
 * Os eventos de formulário levam o prefixo lead_ para não colidir com os
 * form_start / form_submit que o Enhanced measurement do GA4 dispara sozinho
 * em qualquer formulário da página. Ver bindLeadFunnel().
 *
 * Checkout (begin_checkout confirmado, add_payment_info, add_shipping_info,
 * purchase) é responsabilidade do Custom Pixel — o tema não roda no checkout.
 */

(function () {
  'use strict';

  var DATA = window.SolaGA4Data;
  if (!DATA || !DATA.measurementId) return;

  var CURRENCY = DATA.currency || 'USD';
  var DEBUG = !!DATA.debug;

  // Snapshot do carrinho: usado para diferenciar add de remove nas chamadas a
  // /cart/change.js, que retornam o carrinho inteiro sem dizer o que mudou.
  var cartSnapshot = {};

  /* ------------------------------------------------------------------ */
  /* Utilitários                                                         */
  /* ------------------------------------------------------------------ */

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    console.log.apply(console, ['%c[SOLA GA4]', 'color:#7c5cff;font-weight:bold'].concat(args));
  }

  function gtag() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(arguments);
  }

  function track(eventName, params) {
    // O design mode do theme editor geraria eventos falsos a cada render.
    if (DATA.designMode) {
      log('ignorado (design mode):', eventName);
      return;
    }
    var payload = Object.assign({ currency: CURRENCY }, params || {});
    gtag('event', eventName, payload);
    log(eventName, payload);
  }

  function round2(n) {
    return Math.round((Number(n) || 0) * 100) / 100;
  }

  function itemsValue(items) {
    return round2((items || []).reduce(function (sum, it) {
      return sum + (Number(it.price) || 0) * (Number(it.quantity) || 1);
    }, 0));
  }

  /**
   * Converte um line item da Cart API do Shopify para o formato de item GA4.
   *
   * item_id é SEMPRE o variant_id, nunca o SKU. O catálogo da Galo tem SKU
   * ausente em parte dos produtos e, onde existe, às vezes repete o nome da
   * variante ("Blue / Siesta3") em vez de um código. Usar SKU faria o mesmo
   * produto chegar ao GA4 com identidade diferente conforme a página, e o
   * funil de produto nunca fecharia. O SKU vai junto como item_sku, para
   * quem precisar cruzar com o ERP.
   */
  function lineItemToGA4(line) {
    return {
      item_id: String(line.variant_id || line.id),
      item_sku: line.sku || '',
      item_name: line.product_title || line.title,
      item_brand: line.vendor,
      item_category: line.product_type,
      item_variant: line.variant_title,
      price: round2((line.final_price != null ? line.final_price : line.price) / 100),
      quantity: line.quantity || 1,
      item_product_id: String(line.product_id || ''),
      item_variant_id: String(line.variant_id || line.id || '')
    };
  }

  function snapshotFromItems(items) {
    var snap = {};
    (items || []).forEach(function (it) {
      var key = String(it.item_variant_id || it.item_id);
      snap[key] = Object.assign({}, it);
    });
    return snap;
  }

  /* ------------------------------------------------------------------ */
  /* Atribuição — de onde veio o lead                                    */
  /* ------------------------------------------------------------------ */

  /**
   * POR QUE ESTE MÓDULO EXISTE
   * O GA4 resolve atribuição sozinho DENTRO do GA4. O problema é o outro lado:
   * os formulários fazem POST nativo e o Shopify cria um customer que não
   * guarda origem nenhuma. Sem isso, ao cruzar GA4 com Shopify no dashboard,
   * não há como dizer qual campanha gerou qual lead — só dá para comparar
   * totais e torcer para baterem.
   *
   * A solução é carimbar a origem nas tags do próprio customer, no momento do
   * cadastro. Aí o lead nasce com a campanha colada nele e o cruzamento passa
   * a ser exato, não estatístico.
   *
   * Guardamos two touches: o PRIMEIRO (quem descobriu a marca, 90 dias) e o
   * ÚLTIMO (quem fechou). Campanha de lançamento costuma ter jornada longa —
   * atribuir tudo ao último clique subestima o topo de funil.
   */

  var ATTR_FIRST_KEY = 'sola_attr_first';
  var ATTR_LAST_KEY = 'sola_attr_last';
  var ATTR_TTL_MS = 90 * 24 * 60 * 60 * 1000;

  function sanitizeTagValue(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 40);
  }

  /** Lê a origem da URL atual; cai para referrer/direct quando não há UTM. */
  function readAttributionFromUrl() {
    var params = new URLSearchParams(window.location.search);
    var attr = {
      source: params.get('utm_source') || '',
      medium: params.get('utm_medium') || '',
      campaign: params.get('utm_campaign') || '',
      content: params.get('utm_content') || '',
      term: params.get('utm_term') || '',
      timestamp: Date.now(),
      landing: window.location.pathname
    };

    // Cliques de anúncio nem sempre carregam UTM — os click ids denunciam
    // a origem paga e evitam que a venda seja creditada como orgânica.
    var gclid = params.get('gclid');
    var fbclid = params.get('fbclid');
    var ttclid = params.get('ttclid');

    if (!attr.source) {
      if (gclid) { attr.source = 'google'; attr.medium = 'cpc'; }
      else if (fbclid) { attr.source = 'facebook'; attr.medium = 'paid-social'; }
      else if (ttclid) { attr.source = 'tiktok'; attr.medium = 'paid-social'; }
    }
    if (gclid) attr.gclid = gclid;
    if (fbclid) attr.fbclid = fbclid;

    // Sem UTM e sem click id: classifica pelo referrer.
    if (!attr.source) {
      var ref = document.referrer;
      if (ref) {
        try {
          var refHost = new URL(ref).hostname.replace(/^www\./, '');
          if (refHost && refHost !== window.location.hostname.replace(/^www\./, '')) {
            attr.source = refHost;
            attr.medium = 'referral';
          }
        } catch (e) { /* referrer malformado */ }
      }
    }

    if (!attr.source) { attr.source = 'direct'; attr.medium = 'none'; }
    return attr;
  }

  function storageGet(store, key) {
    try {
      var raw = store.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function storageSet(store, key, value) {
    try { store.setItem(key, JSON.stringify(value)); } catch (e) { /* modo privado */ }
  }

  /**
   * Persiste first e last touch. Uma visita direta NÃO sobrescreve o last
   * touch — senão qualquer retorno pelo histórico do navegador apagaria a
   * campanha que realmente trouxe a pessoa.
   */
  function persistAttribution() {
    var current = readAttributionFromUrl();
    var isDirect = current.source === 'direct';

    var first = storageGet(window.localStorage, ATTR_FIRST_KEY);
    if (!first || (Date.now() - first.timestamp) > ATTR_TTL_MS) {
      if (!isDirect || !first) storageSet(window.localStorage, ATTR_FIRST_KEY, current);
    }

    var last = storageGet(window.localStorage, ATTR_LAST_KEY);
    if (!isDirect || !last) storageSet(window.localStorage, ATTR_LAST_KEY, current);
  }

  function getAttribution() {
    return {
      first: storageGet(window.localStorage, ATTR_FIRST_KEY) || readAttributionFromUrl(),
      last: storageGet(window.localStorage, ATTR_LAST_KEY) || readAttributionFromUrl()
    };
  }

  /** Parâmetros de atribuição para acompanhar o evento no GA4. */
  function attributionParams() {
    var attr = getAttribution();
    return {
      attr_first_source: attr.first.source,
      attr_first_medium: attr.first.medium,
      attr_first_campaign: attr.first.campaign,
      attr_last_source: attr.last.source,
      attr_last_medium: attr.last.medium,
      attr_last_campaign: attr.last.campaign,
      attr_landing_page: attr.first.landing
    };
  }

  /**
   * Injeta a origem no input contact[tags] dos formulários de lead, para que
   * o customer nasça no Shopify já carimbado com a campanha.
   *
   * Idempotente de propósito: a sola-galo-subscribe já injeta a própria tag
   * de ?source= nesse mesmo input, e não podemos duplicar nem sobrescrever.
   */
  function stampAttributionOnForms() {
    if (!settingsFlag('leadAttributionTags', true)) return;

    var attr = getAttribution().last;
    var newTags = [
      'src-' + sanitizeTagValue(attr.source),
      attr.medium ? 'mdm-' + sanitizeTagValue(attr.medium) : '',
      attr.campaign ? 'cmp-' + sanitizeTagValue(attr.campaign) : ''
    ].filter(Boolean);

    document.querySelectorAll('input[name="contact[tags]"]').forEach(function (input) {
      var existing = (input.value || '').split(',')
        .map(function (t) { return t.trim(); })
        .filter(Boolean);
      var lower = existing.map(function (t) { return t.toLowerCase(); });

      newTags.forEach(function (tag) {
        if (lower.indexOf(tag) === -1) existing.push(tag);
      });

      input.value = existing.join(', ');
    });

    log('tags de atribuição aplicadas:', newTags.join(', '));
  }

  function settingsFlag(name, fallback) {
    return DATA.flags && DATA.flags[name] != null ? DATA.flags[name] : fallback;
  }

  /* ------------------------------------------------------------------ */
  /* Inicialização: gtag config + Consent Mode                           */
  /* ------------------------------------------------------------------ */

  function init() {
    gtag('js', new Date());

    var config = {
      send_page_view: true,
      currency: CURRENCY,
      page_template: DATA.template
    };

    // user_id habilita relatório cross-device sem enviar PII crua.
    if (DATA.customer && DATA.customer.id) {
      config.user_id = String(DATA.customer.id);
      gtag('set', 'user_properties', {
        customer_orders_count: DATA.customer.ordersCount,
        customer_lifetime_value: DATA.customer.totalSpent,
        customer_type: DATA.customer.ordersCount > 0 ? 'returning' : 'new'
      });
    }

    if (DEBUG) config.debug_mode = true;

    gtag('config', DATA.measurementId, config);
    log('inicializado', DATA.measurementId, 'template:', DATA.template);

    cartSnapshot = snapshotFromItems(DATA.cart && DATA.cart.items);
    syncConsent();
  }

  /**
   * Consent Mode v2 ligado à Customer Privacy API do Shopify.
   * Os defaults do snippet são "denied" na EEA/UK; aqui promovemos para
   * "granted" assim que a API confirmar o consentimento do visitante.
   */
  function syncConsent() {
    function apply() {
      var api = window.Shopify && window.Shopify.customerPrivacy;
      if (!api || typeof api.analyticsProcessingAllowed !== 'function') return false;

      var analytics = api.analyticsProcessingAllowed();
      var marketing = api.marketingAllowed();

      gtag('consent', 'update', {
        analytics_storage: analytics ? 'granted' : 'denied',
        ad_storage: marketing ? 'granted' : 'denied',
        ad_user_data: marketing ? 'granted' : 'denied',
        ad_personalization: marketing ? 'granted' : 'denied'
      });
      log('consent atualizado — analytics:', analytics, 'marketing:', marketing);
      return true;
    }

    if (apply()) return;

    // A API carrega de forma assíncrona; reavaliamos quando ela aparecer e
    // sempre que o visitante alterar a escolha no banner.
    document.addEventListener('visitorConsentCollected', apply);
    var tries = 0;
    var poll = setInterval(function () {
      if (apply() || ++tries > 20) clearInterval(poll);
    }, 250);
  }

  /* ------------------------------------------------------------------ */
  /* view_item — PDP                                                     */
  /* ------------------------------------------------------------------ */

  function currentVariantId() {
    // A URL manda: o tema atualiza ?variant= a cada troca de variante.
    var fromUrl = new URLSearchParams(window.location.search).get('variant');
    if (fromUrl) return fromUrl;
    return DATA.product && DATA.product.selectedVariantId
      ? String(DATA.product.selectedVariantId)
      : null;
  }

  function productItem(variantId) {
    if (!DATA.product) return null;
    var v = DATA.product.variants[String(variantId)];
    if (!v) {
      var keys = Object.keys(DATA.product.variants);
      v = keys.length ? DATA.product.variants[keys[0]] : null;
    }
    return v ? Object.assign({ quantity: 1 }, v) : null;
  }

  function trackViewItem() {
    if (DATA.template !== 'product' || !DATA.product) return;
    var item = productItem(currentVariantId());
    if (!item) return;
    track('view_item', { value: round2(item.price), items: [item] });
  }

  /* ------------------------------------------------------------------ */
  /* view_item_list + select_item — coleção e busca                      */
  /* ------------------------------------------------------------------ */

  function trackViewItemList() {
    var list = DATA.itemList;
    if (!list) return;
    var items = (list.items || []).filter(Boolean).map(function (it) {
      return Object.assign({ item_list_id: list.listId, item_list_name: list.listName }, it);
    });
    if (!items.length) return;

    track('view_item_list', {
      item_list_id: list.listId,
      item_list_name: list.listName,
      items: items
    });
  }

  /**
   * select_item: casa o clique com o item da lista pelo handle na URL.
   * Evita ter que instrumentar cada card de produto individualmente.
   */
  function bindSelectItem() {
    var list = DATA.itemList;
    if (!list || !list.items) return;

    var byHandle = {};
    list.items.filter(Boolean).forEach(function (it) {
      if (it.handle) byHandle[it.handle] = it;
    });

    document.addEventListener('click', function (event) {
      var link = event.target.closest && event.target.closest('a[href*="/products/"]');
      if (!link) return;

      var match = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
      if (!match) return;

      var item = byHandle[match[1]];
      if (!item) return;

      track('select_item', {
        item_list_id: list.listId,
        item_list_name: list.listName,
        items: [Object.assign({ item_list_id: list.listId, item_list_name: list.listName }, item)]
      });
    }, true);
  }

  /* ------------------------------------------------------------------ */
  /* add_to_cart / remove_from_cart — interceptação da Cart API          */
  /* ------------------------------------------------------------------ */

  function handleCartAdd(responseData) {
    // /cart/add.js devolve o line item direto (form) ou {items:[...]} (bulk).
    var added = responseData && responseData.items
      ? responseData.items
      : (responseData && (responseData.variant_id || responseData.id) ? [responseData] : []);
    if (!added.length) return;

    var items = added.map(lineItemToGA4);
    track('add_to_cart', { value: itemsValue(items), items: items });

    // Mantém o snapshot alinhado para que o próximo change.js compare certo.
    items.forEach(function (it) {
      var key = String(it.item_variant_id || it.item_id);
      if (cartSnapshot[key]) {
        cartSnapshot[key].quantity += it.quantity;
      } else {
        cartSnapshot[key] = Object.assign({}, it);
      }
    });
  }

  /**
   * /cart/change.js e /cart/update.js retornam o carrinho completo. Comparamos
   * com o snapshot anterior para deduzir o delta e emitir add ou remove.
   */
  function handleCartMutation(cartData) {
    if (!cartData || !cartData.items) return;

    var next = {};
    cartData.items.forEach(function (line) {
      var it = lineItemToGA4(line);
      next[String(it.item_variant_id || it.item_id)] = it;
    });

    var added = [];
    var removed = [];

    Object.keys(next).forEach(function (key) {
      var before = cartSnapshot[key];
      var delta = next[key].quantity - (before ? before.quantity : 0);
      if (delta > 0) added.push(Object.assign({}, next[key], { quantity: delta }));
      else if (delta < 0) removed.push(Object.assign({}, next[key], { quantity: -delta }));
    });

    // Linhas que sumiram por completo do carrinho.
    Object.keys(cartSnapshot).forEach(function (key) {
      if (!next[key]) removed.push(Object.assign({}, cartSnapshot[key]));
    });

    if (added.length) track('add_to_cart', { value: itemsValue(added), items: added });
    if (removed.length) track('remove_from_cart', { value: itemsValue(removed), items: removed });

    cartSnapshot = next;
  }

  function interceptCartApi() {
    var originalFetch = window.fetch;
    if (typeof originalFetch !== 'function') return;

    window.fetch = function (input, options) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var promise = originalFetch.apply(this, arguments);

      if (!/\/cart\/(add|change|update)(\.js)?/.test(url)) return promise;

      promise.then(function (response) {
        if (!response.ok) return;
        // clone() é obrigatório: o body só pode ser lido uma vez e quem
        // chamou o fetch precisa dele intacto.
        response.clone().json().then(function (data) {
          if (/\/cart\/add/.test(url)) handleCartAdd(data);
          else handleCartMutation(data);
        }).catch(function () { /* resposta não-JSON, ignora */ });
      }).catch(function () { /* falha de rede, ignora */ });

      return promise;
    };

    log('Cart API interceptada');
  }

  /* ------------------------------------------------------------------ */
  /* view_cart e begin_checkout                                          */
  /* ------------------------------------------------------------------ */

  function trackViewCart() {
    if (DATA.template !== 'cart') return;
    var items = (DATA.cart && DATA.cart.items) || [];
    if (!items.length) return;
    track('view_cart', { value: round2(DATA.cart.totalPrice), items: items });
  }

  /**
   * begin_checkout no tema é um sinal de INTENÇÃO — dispara no clique.
   * O evento confirmado (usuário chegou de fato ao checkout) vem do Custom
   * Pixel. Se ambos estiverem ativos, mantenha só um deles como conversão
   * no GA4 para não contar duas vezes.
   */
  function bindBeginCheckout() {
    document.addEventListener('click', function (event) {
      var el = event.target.closest && event.target.closest(
        '[name="checkout"], a[href*="/checkout"], button[href*="/checkout"]'
      );
      if (!el) return;

      var items = Object.keys(cartSnapshot).map(function (k) { return cartSnapshot[k]; });
      if (!items.length) return;

      track('begin_checkout', { value: itemsValue(items), items: items });
    }, true);
  }

  /* ------------------------------------------------------------------ */
  /* generate_lead — os 5 formulários de captação da lead version        */
  /* ------------------------------------------------------------------ */

  /**
   * Os markers de sucesso são renderizados server-side por
   * {% if form.posted_successfully? %} — só existem quando o Shopify
   * confirmou o cadastro. Trackear no submit inflaria o número com
   * validações que falharam.
   */
  var LEAD_MARKERS = [
    '[data-just-subscribed]',        // sola-follow-the-launch, sola-pre-order, sola-sumo-launch
    '[data-ws-just-subscribed]',     // sola-galo-subscribe
    '.sola-flaunch__success'         // sola-launch-cta (sem marker dedicado)
  ].join(',');

  /** Identifica de qual section de captação o formulário faz parte. */
  function leadContext(element) {
    var section = element.closest('[id^="shopify-section-"]');
    var sectionId = section ? section.id.replace('shopify-section-', '') : 'unknown';
    var sectionType = section
      ? (section.className.match(/sola-[a-z-]+/) || [])[0] || sectionId
      : sectionId;
    return { id: sectionId, type: sectionType };
  }

  /**
   * Etapas intermediárias do funil de lead.
   *
   * Sem elas o funil tem só dois pontos — visitou e converteu — e não dá para
   * distinguir "ninguém viu o formulário" de "todo mundo viu e desistiu de
   * preencher". São diagnósticos opostos: um é problema de layout, o outro é
   * problema de oferta ou de fricção no campo.
   *
   *   lead_form_view  → o formulário entrou de fato na viewport
   *   lead_form_start → houve o primeiro foco em um campo (intenção real)
   *
   * O prefixo lead_ não é enfeite. O Enhanced measurement do GA4 já dispara
   * form_start e form_submit sozinho, em QUALQUER formulário da página —
   * busca do header, login, newsletter genérica. Se usássemos o mesmo nome,
   * os dois fluxos se misturariam num único evento com significados
   * diferentes, e o funil de lead viraria um número impossível de ler.
   * Com nome próprio, os dois convivem e o Enhanced measurement pode ficar
   * inteiramente ligado.
   */
  function bindLeadFunnel() {
    var forms = document.querySelectorAll('form[action*="/contact"], form[action*="/account"]');
    if (!forms.length) return;

    var observer = 'IntersectionObserver' in window
      ? new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);

            var ctx = leadContext(entry.target);
            track('lead_form_view', Object.assign({
              form_source: ctx.type,
              form_section_id: ctx.id
            }, attributionParams()));
          });
        }, { threshold: 0.5 })
      : null;

    var bound = 0;

    forms.forEach(function (form) {
      if (!form.querySelector('input[name="contact[email]"]')) return;

      // Marca no próprio nó: esta função roda de novo a cada
      // shopify:section:load, e sem a trava cada recarregamento somaria mais
      // um listener no mesmo formulário — inflando form_start.
      if (form.dataset.solaGa4Bound === 'true') return;
      form.dataset.solaGa4Bound = 'true';
      bound++;

      if (observer) observer.observe(form);

      // `once` garante o disparo único mesmo se o visitante navegar entre os
      // campos: o que interessa é a primeira intenção de preencher.
      form.addEventListener('focusin', function () {
        var ctx = leadContext(form);
        track('lead_form_start', Object.assign({
          form_source: ctx.type,
          form_section_id: ctx.id
        }, attributionParams()));
      }, { once: true });
    });

    if (bound) log('funil de lead observando', bound, 'formulário(s)');
  }

  /**
   * Um caminho só para registrar o lead, venha ele de um marker no HTML ou
   * de um aviso em JavaScript. A trava de sessão é a mesma nos dois, então
   * uma captura que chegue pelas duas portas conta uma vez só.
   */
  function registrarLead(sectionId, sectionType, tags, page) {
    // Um reload da URL com ?customer_posted=true re-renderiza o marker.
    // A trava de sessão impede o lead duplicado.
    var key = 'sola_ga4_lead_' + sectionId;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch (e) { /* modo privado, segue sem trava */ }

    track('generate_lead', Object.assign({
      lead_source: sectionType,
      lead_section_id: sectionId,
      lead_tags: tags || '',
      lead_page: page || window.location.pathname
    }, attributionParams()));

    // sign_up espelha o lead para quem preferir o evento canônico do GA4.
    track('sign_up', { method: 'shopify_customer_form' });
  }

  function trackLeads() {
    document.querySelectorAll(LEAD_MARKERS).forEach(function (marker) {
      var section = marker.closest('[id^="shopify-section-"]');
      var sectionId = section ? section.id.replace('shopify-section-', '') : 'unknown';
      var form = marker.closest('form');
      var tagsInput = form && form.querySelector('input[name="contact[tags]"]');
      var sectionType = section
        ? (section.className.match(/sola-[a-z-]+/) || [])[0] || sectionId
        : sectionId;

      registrarLead(sectionId, sectionType, tagsInput ? tagsInput.value : '');
    });
  }

  /**
   * Captura que não deixa marker no HTML.
   *
   * O popup envia de forma nativa (o captcha invisível da Shopify recusa
   * fetch), a página recarrega com ?customer_posted=true e ele mostra o
   * agradecimento por JavaScript. Não existe {% if form.posted_successfully? %}
   * renderizando um marker, então trackLeads() não tem o que encontrar e o
   * lead do popup nunca era contado. Medido em 16-17/09: 4.329 formulários
   * vistos e 193 preenchimentos iniciados nos popups, com zero generate_lead.
   *
   * O aviso chega por dois caminhos de propósito. Este arquivo carrega com
   * `defer` e portanto roda DEPOIS do script inline da seção: um
   * addEventListener sozinho chegaria tarde e perderia o disparo que já
   * aconteceu. A fila cobre o que passou, o listener cobre o que vier
   * (section recarregada por AJAX, por exemplo).
   */
  function trackLeadsAvisados() {
    var fila = window.solaCapturados;
    if (fila && fila.length) {
      fila.splice(0).forEach(anotarCaptura);
    }
    // a partir daqui a fila vira um canal direto
    window.solaCapturados = { push: anotarCaptura, length: 0 };
    document.addEventListener('sola:capturado', function (e) {
      if (e && e.detail) anotarCaptura(e.detail);
    });
  }

  function anotarCaptura(info) {
    if (!info || !info.sectionId) return;

    var tags = info.tags;
    if (tags == null) {
      // Lido agora, e não no aviso. Quem avisa é o script inline da seção,
      // que roda durante a análise da página -- antes de
      // stampAttributionOnForms(). Naquele instante o input ainda tem só a
      // tag fixa da seção, sem a origem. Aqui já tem.
      var sec = document.getElementById('shopify-section-' + info.sectionId);
      var input = sec && sec.querySelector('input[name="contact[tags]"]');
      tags = input ? input.value : '';
    }

    registrarLead(info.sectionId, info.sectionType || 'desconhecido', tags, info.page);
  }

  /* ------------------------------------------------------------------ */
  /* search                                                              */
  /* ------------------------------------------------------------------ */

  function trackSearch() {
    if (!DATA.searchTerms) return;
    track('search', {
      search_term: DATA.searchTerms,
      search_results_count: DATA.searchResultsCount
    });
  }

  /* ------------------------------------------------------------------ */
  /* Bootstrap                                                           */
  /* ------------------------------------------------------------------ */

  function start() {
    // A atribuição é gravada antes de qualquer evento, para que todos possam
    // carregar a origem já resolvida.
    persistAttribution();

    init();
    interceptCartApi();

    trackViewItem();
    trackViewItemList();
    trackViewCart();
    trackSearch();

    // O carimbo vem ANTES de trackLeads de propósito. Na ordem antiga o lead
    // era lido primeiro e `lead_tags` saía sem a origem -- medido em 16-17/09:
    // 204 leads, nenhum com src-. O cliente na Shopify continuava carimbado,
    // porque o carimbo foi aplicado na página anterior, antes do envio; quem
    // reportava errado era o GA4.
    stampAttributionOnForms();

    trackLeads();
    trackLeadsAvisados();

    bindSelectItem();
    bindBeginCheckout();
    bindLeadFunnel();

    // O tema recarrega sections via AJAX (drawer, filtros); reavaliamos os
    // markers de lead e recarimbamos os formulários que vierem novos.
    document.addEventListener('shopify:section:load', function () {
      stampAttributionOnForms();
      trackLeads();
      bindLeadFunnel();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
